import { Link } from "@tanstack/react-router";
import { cn } from "../../lib/cn.js";
import type { HealthResponse } from "../../lib/queries.js";
import { Section } from "../ui/Section.js";

/**
 * What is waiting for a human, and what stays missing until one shows up.
 *
 * A blocked symbol is absent from the screen rather than wrongly ranked, so
 * a growing queue is a shrinking universe. Saying how many is what makes the
 * trade visible.
 */
export function ReviewQueueCard({
  review,
  counts,
}: {
  review: HealthResponse["review"];
  counts: Record<string, number>;
}) {
  return (
    <Section title="Corporate-action review">
      <dl className="flex flex-col gap-1 text-sm text-text-muted">
        <div className="flex justify-between gap-4">
          <dt>Open items</dt>
          <dd className={cn("tabular-nums", review.openItems > 0 ? "text-warning" : "text-text")}>
            {review.openItems}
          </dd>
        </div>
        <div className="flex justify-between gap-4">
          <dt>Symbols blocked from adjustment</dt>
          <dd
            className={cn("tabular-nums", review.blockedSymbols > 0 ? "text-danger" : "text-text")}
          >
            {review.blockedSymbols}
          </dd>
        </div>
      </dl>

      {review.blockedSymbols > 0 ? (
        <p className="text-xs text-text-muted">
          Blocked names are missing from the screen, not mis-ranked in it.{" "}
          <Link to="/screen" className="text-accent hover:underline">
            Open the screen
          </Link>{" "}
          to see what is there instead.
        </p>
      ) : null}

      <dl className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1 border-t border-border pt-2 text-xs text-text-muted">
        {Object.entries(counts).map(([table, count]) => (
          <div key={table} className="flex justify-between gap-2">
            <dt>{table}</dt>
            <dd className="text-text tabular-nums">{count.toLocaleString("en-IN")}</dd>
          </div>
        ))}
      </dl>
    </Section>
  );
}
